// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel'; // Make sure you copied this file into your repo

import { MultiDaySlot } from './MultiDaySlot';
import { StructuredAddress } from './StructuredAddress';
import { TeamId } from './TeamId';
import { LatLng } from './LatLng';
import { Contact } from './Contact';
import { ContactId } from './ContactId';
import { UserId } from './UserId';
import { VehicleModelId } from './VehicleModelId';
import { SkillId } from './SkillId';
import { GeocodingMetadata } from './GeocodingMetadata';
import { MasterVehicle } from 'common/models/flow/MasterVehicle';
import { AuditMetadata } from './AuditMetadata';
import { LocationLearningMetadata } from './LocationLearning';
import { LocationCrossDockInfo } from './LocationCrossDockInfo';

import type { ContactIdModelType } from 'common/models/flow/ContactId';
import type { ContactModelType } from 'common/models/flow/Contact';
import type { LatLngModelType } from 'common/models/flow/LatLng';
import type { StructuredAddressModelType } from 'common/models/flow/StructuredAddress';
import type { TeamIdModelType } from 'common/models/flow/TeamId';
import type { MultiDaySlotModelType } from 'common/models/flow/MultiDaySlot';
import type { UserIdModelType } from 'common/models/flow/UserId';
import type { VehicleModelIdModelType } from 'common/models/flow/VehicleModelId';
import type { SkillIdModelType } from 'common/models/flow/SkillId';
import type { GeocodingMetadataModelType } from './GeocodingMetadata';
import type { AuditMetadataModelType } from './AuditMetadata';
import type { LocationLearningMetadataModelType } from './LocationLearning';
import type { MasterVehicleModelType } from 'common/models/flow/MasterVehicle';
import type { LocationCrossDockInfoModelType } from './LocationCrossDockInfo';

export type StatusEnum = 'ACTIVE' | 'INACTIVE';
export type LocationTypeEnum =
    | 'CLIENT'
    | 'KITCHEN'
    | 'WAREHOUSE'
    | 'MANUFACTURING_PLANT'
    | 'SUPPLIER'
    | 'RETAILER'
    | 'CROSS_DOCK';

export type MasterLocationModelType = {
    clientId: string,
    id: string,
    code: string,
    name: string,
    status: StatusEnum,
    type: LocationTypeEnum,
    teams: Array<TeamIdModelType>,
    address: ?StructuredAddressModelType,
    latLng: ?LatLngModelType,
    geocodingMetadata: ?GeocodingMetadataModelType,
    contactId: ?ContactIdModelType,
    contact: ?ContactModelType,
    timeSlots: Array<MultiDaySlotModelType>,
    transactionDuration: number,
    users: Array<UserIdModelType>,
    skills: Array<SkillIdModelType>,
    vehicleModels: Array<VehicleModelIdModelType>,
    customProperties: { [key: string]: string },
    shouldGeofence: boolean,
    pincodes: Array<string>,
    version: number,
    geocoded: ?boolean,
    auditMetadata: ?AuditMetadataModelType,
    locationLearningAudit: Array<LocationLearningMetadataModelType>,
    priority: ?number,
    multiDaySlotSpan: ?number,
    crossDockInfo: LocationCrossDockInfoModelType,
    vehicleModelId: ?VehicleModelIdModelType,
    vehicleModel: ?MasterVehicleModelType
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MasterLocation extends ImmutableModel {
    static fromJS(json: MasterLocationModelType): MasterLocation {
        const state: Object = Object.assign({}, json);
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.address = state.address
            ? StructuredAddress.fromJS(state.address)
            : state.address;
        state.latLng = state.latLng
            ? LatLng.fromJS(state.latLng)
            : state.latLng;
        state.geocodingMetadata = state.geocodingMetadata
            ? GeocodingMetadata.fromJS(state.geocodingMetadata)
            : state.geocodingMetadata;
        state.contactId = state.contactId
            ? ContactId.fromJS(state.contactId)
            : state.contactId;
        state.contact = state.contact
            ? Contact.fromJS(state.contact)
            : state.contact;
        state.timeSlots = Immutable.List(state.timeSlots).map(item =>
            MultiDaySlot.fromJS(item)
        );
        state.users = Immutable.List(state.users).map(item =>
            UserId.fromJS(item)
        );
        state.skills = Immutable.List(state.skills).map(item =>
            SkillId.fromJS(item)
        );
        state.vehicleModels = Immutable.List(state.vehicleModels).map(item =>
            VehicleModelId.fromJS(item)
        );
        state.customProperties = Immutable.Map(state.customProperties);
        state.pincodes = Immutable.List(state.pincodes);
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.locationLearningAudit = Immutable.List(
            state.locationLearningAudit
        ).map(item => LocationLearningMetadata.fromJS(item));
        state.crossDockInfo = LocationCrossDockInfo.fromJS(state.crossDockInfo);
        state.vehicleModelId = state.vehicleModelId
            ? VehicleModelId.fromJS(state.vehicleModelId)
            : state.vehicleModelId;
        state.vehicleModel = state.vehicleModel
            ? MasterVehicle.fromJS(state.vehicleModel)
            : state.vehicleModel;
        return new this(Immutable.Map(state));
    }

    toJS(): MasterLocationModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            code: this.code,
            name: this.name,
            status: this.status,
            type: this.type,
            teams: this.teams.toArray().map(item => item.toJS()),
            address: this.address ? this.address.toJS() : this.address,
            latLng: this.latLng ? this.latLng.toJS() : this.latLng,
            geocodingMetadata: this.geocodingMetadata
                ? this.geocodingMetadata.toJS()
                : this.geocodingMetadata,
            contactId: this.contactId ? this.contactId.toJS() : this.contactId,
            contact: this.contact ? this.contact.toJS() : this.contact,
            timeSlots: this.timeSlots.toArray().map(item => item.toJS()),
            transactionDuration: this.transactionDuration,
            users: this.users.toArray().map(item => item.toJS()),
            skills: this.skills.toArray().map(item => item.toJS()),
            vehicleModels: this.vehicleModels
                .toArray()
                .map(item => item.toJS()),
            customProperties: this.customProperties.toObject(),
            shouldGeofence: this.shouldGeofence,
            pincodes: this.pincodes.toArray(),
            version: this.version,
            geocoded: this.geocoded,
            auditMetadata: this.auditMetadata
                ? this.auditMetadata.toJS()
                : this.auditMetadata,
            locationLearningAudit: this.locationLearningAudit
                .toArray()
                .map(item => item.toJS()),
            priority: this.priority,
            multiDaySlotSpan: this.multiDaySlotSpan,
            crossDockInfo: this.crossDockInfo.toJS(),
            vehicleModelId: this.vehicleModelId
                ? this.vehicleModelId.toJS()
                : this.vehicleModelId,
            vehicleModel: this.vehicleModel
                ? this.vehicleModel.toJS()
                : this.vehicleModel
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }

    setStatus(status: StatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get type(): LocationTypeEnum {
        return this._state.get('type');
    }

    setType(type: LocationTypeEnum): this {
        return this.clone(this._state.set('type', type));
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    setTeams(teams: Immutable.List<TeamId>): this {
        return this.clone(this._state.set('teams', teams));
    }

    get address(): ?StructuredAddress {
        return this._state.get('address');
    }

    setAddress(address: ?StructuredAddress): this {
        return this.clone(this._state.set('address', address));
    }

    get latLng(): ?LatLng {
        return this._state.get('latLng');
    }

    setLatLng(latLng: ?LatLng): this {
        return this.clone(this._state.set('latLng', latLng));
    }

    get geocodingMetadata(): ?GeocodingMetadata {
        return this._state.get('geocodingMetadata');
    }

    setGeocodingMetadata(geocodingMetadata: ?GeocodingMetadata): this {
        return this.clone(
            this._state.set('geocodingMetadata', geocodingMetadata)
        );
    }

    get contactId(): ?ContactId {
        return this._state.get('contactId');
    }

    setContactId(contactId: ?ContactId): this {
        return this.clone(this._state.set('contactId', contactId));
    }

    get contact(): ?Contact {
        return this._state.get('contact');
    }

    setContact(contact: ?Contact): this {
        return this.clone(this._state.set('contact', contact));
    }

    get timeSlots(): Immutable.List<MultiDaySlot> {
        return this._state.get('timeSlots');
    }

    setTimeSlots(timeSlots: Immutable.List<MultiDaySlot>): this {
        return this.clone(this._state.set('timeSlots', timeSlots));
    }

    get transactionDuration(): number {
        return this._state.get('transactionDuration');
    }

    setTransactionDuration(transactionDuration: number): this {
        return this.clone(
            this._state.set('transactionDuration', transactionDuration)
        );
    }

    get users(): Immutable.List<UserId> {
        return this._state.get('users');
    }

    setUsers(users: Immutable.List<UserId>): this {
        return this.clone(this._state.set('users', users));
    }

    get skills(): Immutable.List<SkillId> {
        return this._state.get('skills');
    }

    setSkills(skills: Immutable.List<SkillId>): this {
        return this.clone(this._state.set('skills', skills));
    }

    get vehicleModels(): Immutable.List<VehicleModelId> {
        return this._state.get('vehicleModels');
    }

    setVehicleModels(vehicleModels: Immutable.List<VehicleModelId>): this {
        return this.clone(this._state.set('vehicleModels', vehicleModels));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get shouldGeofence(): boolean {
        return this._state.get('shouldGeofence');
    }

    setShouldGeofence(shouldGeofence: boolean): this {
        return this.clone(this._state.set('shouldGeofence', shouldGeofence));
    }

    get pincodes(): Immutable.List<string> {
        return this._state.get('pincodes');
    }

    setPincodes(pincodes: Immutable.List<string>): this {
        return this.clone(this._state.set('pincodes', pincodes));
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }

    get geocoded(): ?boolean {
        return this._state.get('geocoded');
    }

    setGeocoded(geocoded: ?boolean): this {
        return this.clone(this._state.set('geocoded', geocoded));
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: ?AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get locationLearningAudit(): Immutable.List<LocationLearningMetadata> {
        return this._state.get('locationLearningAudit');
    }

    setLocationLearningAudit(
        locationLearningAudit: Immutable.List<LocationLearningMetadata>
    ): this {
        return this.clone(
            this._state.set('locationLearningAudit', locationLearningAudit)
        );
    }

    get priority(): ?number {
        return this._state.get('priority');
    }

    setPriority(priority: ?number): this {
        return this.clone(this._state.set('priority', priority));
    }

    get multiDaySlotSpan(): ?number {
        return this._state.get('multiDaySlotSpan');
    }

    setMultiDaySlotSpan(multiDaySlotSpan: ?number): this {
        return this.clone(
            this._state.set('multiDaySlotSpan', multiDaySlotSpan)
        );
    }

    get crossDockInfo(): LocationCrossDockInfo {
        return this._state.get('crossDockInfo');
    }

    setCrossDockInfo(crossDockInfo: LocationCrossDockInfo): this {
        return this.clone(this._state.set('crossDockInfo', crossDockInfo));
    }

    get vehicleModelId(): ?VehicleModelId {
        return this._state.get('vehicleModelId');
    }

    setVehicleModelId(vehicleModelId: ?VehicleModelId): this {
        return this.clone(this._state.set('vehicleModelId', vehicleModelId));
    }

    get vehicleModel(): ?MasterVehicle {
        return this._state.get('vehicleModel');
    }

    setVehicleModel(vehicleModel: ?MasterVehicle): this {
        return this.clone(this._state.set('vehicleModel', vehicleModel));
    }
}
